import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { withTranslation, WithTranslation } from 'react-i18next';

import { useAssistant } from '@core/assistant/providers/Provider';
import { enableDebugControls } from '@core/assistant/constants';
import { SCREENS } from '@core/assistant/interfaces';
import { useDebug } from '@core/assistant/providers/Debug';
import { useMicRecord } from '@core/assistant/providers/Record';
import { useGreetings } from '@core/assistant/providers/Greetings';
import { log } from '@core/assistant/providers/Debug';

import { useStyles } from './styles';

const DefaultScreen = ({ t: translate }: WithTranslation) => {
  const { generateGreetingsAudio } = useGreetings();
  const classes = useStyles();

  const [showDebugUI, setShowDebugUI] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [showNextButton, setShowNextButtton] = useState(false);
  const intervalTimer = useRef<NodeJS.Timer | null>(null);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const {
    navigate,
    setOpen,
    setNavigationHistory,
    stopPlayback,
    isRecording,
    setIsRecording,
    audioPromptHistory,
    teleportToRegion,
    startFlow,
    linearScriptPlayerRef,
    isAudioPlaybackActive,
    fetchScripts,
    fetchParticipantProgress,
    resetProgress,
  } = useAssistant();

  const {
    queryBedrockTest,
    generateAudioPlaybackTest,
    testKeyboard,
    sendWSMessage,
  } = useDebug();

  const { startRecording, stopRecording } = useMicRecord();

  const handleRecording = () => {
    log('start/stop recording button clicked');

    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }

    setIsRecording(!isRecording);
  };

  const handleTestKeystroke = () => {
    testKeyboard();
  };

  const handleTestTeleport = () => {
    teleportToRegion('auditorium');
  };

  const handleSendWS = () => {
    log('handleSendWS', inputValue);
    sendWSMessage(inputValue);
  };

  const handleNext = () => {
    setShowNextButtton(false);
    linearScriptPlayerRef.current?.triggerNext();
  };

  useEffect(() => {
    log('audioPromptHistory', audioPromptHistory);
    log(
      'new item in audioPromptHistory',
      audioPromptHistory[audioPromptHistory.length - 1],
    );
  }, [audioPromptHistory]);

  useEffect(() => {
    console.warn('isAudioPlaybackActive', isAudioPlaybackActive);

    const linearScriptPlayerState =
      linearScriptPlayerRef.current?.getCurrentState();
    console.warn('linearScriptPlayerState', linearScriptPlayerState);

    if (
      !isAudioPlaybackActive &&
      linearScriptPlayerState?.currentNode.awaitButtonNext
    ) {
      setShowNextButtton(true);
    } else if (!linearScriptPlayerState?.currentNode.awaitButtonNext) {
      setShowNextButtton(false);
    } else if (linearScriptPlayerState?.isCompleted) {
      setShowNextButtton(false);
    }
  }, [isAudioPlaybackActive]);

  // dirty fix to disable Next button when script is completed
  useEffect(() => {
    intervalTimer.current = setInterval(() => {
      const linearScriptPlayerState =
        linearScriptPlayerRef.current?.getCurrentState();

      if (!linearScriptPlayerState?.isRunning) {
        setShowNextButtton(false);
      }
    }, 1000);
  }, []);

  return (
    <Box sx={classes.box}>
      <Button sx={classes.button} onClick={() => navigate(SCREENS.HOW_TO)}>
        {translate('assistant.howTo.title')}
      </Button>
      <Button
        sx={{ ...classes.button, ...classes.blueButton }}
        onClick={() => navigate(SCREENS.CHAT)}
      >
        {translate('assistant.askMe.title')}
      </Button>
      <Button sx={classes.button} onClick={() => handleRecording()}>
        {!isRecording
          ? translate('assistant.voiceAction')
          : translate('assistant.stopRecording')}
      </Button>
      <Button
        sx={classes.button}
        onClick={() => handleNext()}
        disabled={!showNextButton}
      >
        Next
      </Button>
      <Box>
        <FormControlLabel
          control={
            <Switch
              checked={showDebugUI}
              onChange={() => setShowDebugUI(!showDebugUI)}
            />
          }
          sx={{
            color: '#fff',
          }}
          label="Show debug"
        />
      </Box>
      <Button
        sx={classes.hideButton}
        onClick={() => {
          stopPlayback();
          setOpen(false);
          setNavigationHistory([]);
          navigate(SCREENS.DEFAULT);
        }}
      >
        {translate('assistant.hideNoa')}
      </Button>

      {showDebugUI && (
        <Box sx={classes.debugOverlay}>
          <Button sx={classes.debugButton} onClick={() => startFlow('landing')}>
            Start Landing flow
          </Button>
          <Button
            sx={classes.debugButton}
            onClick={() => startFlow('bpSpotRegionEx01')}
          >
            Start Exhibit Hall flow
          </Button>
          <Button
            sx={classes.debugButton}
            onClick={() => startFlow('bpSpotRegionExB01')}
          >
            Start Booth 1 flow
          </Button>
          <Button
            sx={classes.debugButton}
            onClick={() => startFlow('bpSpotRegionExB02')}
          >
            Start Booth 2 flow
          </Button>
          <Button
            sx={classes.debugButton}
            onClick={() => startFlow('bpSpotRegionExB03')}
          >
            Start Booth 3 flow
          </Button>
          <Button
            sx={classes.debugButton}
            onClick={() => startFlow('bpSpotRegionExB04')}
          >
            Start Booth 4 flow
          </Button>
          <Button
            sx={classes.debugButton}
            onClick={() => startFlow('bpSpotRegionExB05')}
          >
            Start Booth 5 flow
          </Button>
          <Button
            sx={classes.debugButton}
            onClick={() => startFlow('bpSpotRegionExB06')}
          >
            Start Booth 6 flow
          </Button>
          <Button
            sx={classes.debugButton}
            onClick={() => startFlow('bpSpotRegionExB07')}
          >
            Start Booth 7 flow
          </Button>
          <Button
            sx={classes.debugButton}
            onClick={() => startFlow('bpSpotRegionExB08')}
          >
            Start Booth 8 flow
          </Button>
          <Button
            sx={classes.debugButton}
            onClick={() => startFlow('bpSpotRegionExB09')}
          >
            Start Booth 9 flow
          </Button>
          <Button
            sx={classes.debugButton}
            onClick={() => startFlow('bpSpotRegionExB10')}
          >
            Start Booth 10 flow
          </Button>
          <Button sx={classes.debugButton} onClick={() => resetProgress()}>
            Reset progress
          </Button>
          <Button
            sx={classes.debugButton}
            onClick={() => generateGreetingsAudio()}
          >
            Greetings test
          </Button>
          <Box
            sx={{
              mt: 1,
            }}
          >
            <Button sx={classes.debugButton} onClick={() => fetchScripts()}>
              Fetch scripts data
            </Button>
            <Button
              sx={classes.debugButton}
              onClick={() => fetchParticipantProgress()}
            >
              Fetch progress data
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default withTranslation('common')(DefaultScreen);
